import "./App.css";
import { RouterProvider } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useRoutes from "./router/index.tsx";
import {
  refreshToken,
  setAuthLoading,
  setUserData,
} from "./store/actions/auth.action";
import { useEffect, useRef, useState } from "react";
import { getCandidateDetails } from "./endpoints/candidate.service";
import advancedFormat from "dayjs/plugin/advancedFormat";
import dayjs from "dayjs";
import { EnumUserTypes } from "@utils/constants";
import { getHiringCompanyDetails } from "@endpoints/company.service";
import { useSnackbar } from "notistack";
import { setCompanies } from "@store/actions/company.action";
import useInstallPrompt from "./hooks/useInstallPrompt";
import InstallPromptModal from "./components/InstallPromptModal";

function IRouter() {
  const { router } = useRoutes();
  return <RouterProvider router={router} />;
}

let socket = null;

function App() {
  dayjs.extend(advancedFormat);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { logged_in, loading, authenticated, user } = useSelector(
    (state) => state.auth,
  );
  const [initialized, setInitialized] = useState(false);
  const {
    isInstallable,
    isDismissed,
    isInstalled,
    isIos,
    isSafari,
    isIosChrome,
    isMac,
    promptInstall,
    dismissInstall,
    installError,
    markAsInstalled,
  } = useInstallPrompt();
  const isManualInstall = isIos || isSafari || isIosChrome || isMac;
  const initiating = useRef(false);

  async function loadInitialData(payload) {
    // load initial data here
    if (payload?.userType === EnumUserTypes.CANDIDATE) {
      // Todo:check
      const { success, data } = await getCandidateDetails();
      if (success) {
        // delete candidateDetails._id;
          const { _id, ...rest } = data;
        dispatch(setUserData({...rest,candidateId:_id}));
      }
      dispatch(setUserData(payload));
    } else {
      dispatch(setUserData(payload));
    }
    if (payload?.userType === EnumUserTypes.HIRING_COMPANY) {
      try {
        const { success, data } = await getHiringCompanyDetails();
        if (success) {
          dispatch(setCompanies(data));
        }
      } catch (error) {
        enqueueSnackbar(error, { variant: "error" });
      }
    }
  }

  // const initiateApp = async () => {
  // initiating.current = true;
  //   const token = localStorage.getItem("refreshToken");

  //   if (!logged_in && token && !authenticated && initiating.current) {
  //     refreshToken(token)(dispatch)
  //       .then((data) => {
  //         if (data) {
  //           // loadInitialData(data?.user);
  //           // Remove socket setup from here
  //           setTimeout(() => {
  //             setInitialized(true);
  //           }, 100);
  //         } else {
  //           window.location.replace("/");
  //         }
  //       })
  //       .finally(() => {
  //         initiating.current = false;
  //       });
  //   } else {
  //     dispatch(setAuthLoading(false));
  //     setTimeout(() => {
  //       setInitialized(true);
  //     }, 100);
  //     initiating.current = false;
  //   }
  // };

  // const initiateApp = async () => {
  //   initiating.current = true;
  //   const token = localStorage.getItem("refreshToken");

  //   try {
  //     if (!logged_in && token && !authenticated) {
  //       const data = await refreshToken(token)(dispatch);

  //       console.log("Refreshed data:", data);

  //       if (data) {
  //         dispatch(setAuthLoading(false)); // 🔹 stop loading
  //         setInitialized(true);
  //       } else {
  //         window.location.replace("/"); // refresh failed
  //       }
  //     } else {
  //       dispatch(setAuthLoading(false)); // 🔹 stop loading
  //       setInitialized(true);
  //     }
  //   } catch (err) {
  //     console.error("❌ App init failed:", err);
  //     dispatch(setAuthLoading(false));
  //     setInitialized(true);
  //   } finally {
  //     initiating.current = false;
  //   }
  // };

  // In App.js
  const initiateApp = async () => {
    initiating.current = true;
    const token = localStorage.getItem("refreshToken");
    const sessionId = localStorage.getItem("sessionId");

    console.log("🔄 App initialization:", {
      hasRefreshToken: !!token,
      hasSessionId: !!sessionId,
      logged_in,
      authenticated,
    });

    try {
      if (!logged_in && token && sessionId && !authenticated) {
        // console.log("🔄 App: Refreshing token and initializing socket...");
        const data = await refreshToken(token)(dispatch);

        if (data) {
          // console.log("✅ App: Refresh successful, user:", data.user?.email);
          await loadInitialData(data?.user);
          dispatch(setUserData({
            clientDeviceId:data?.clientDeviceId
          }))
          // Ensure sessionId is preserved
          if (data.sessionId) {
            localStorage.setItem("sessionId", data.sessionId);
            // console.log("✅ App: SessionId updated");
          }

          dispatch(setAuthLoading(false));
          setInitialized(true);

          // Trigger socket initialization
          setTimeout(() => {
            // console.log("🚀 App: Triggering socket initialization");
            // This will trigger the SocketProvider useEffect
          }, 100);
        } else {
          // console.log("❌ App: Refresh failed, redirecting to login");
          window.location.replace("/");
        }
      } else {
        // console.log("🔄 App: No token or already authenticated");
        dispatch(setAuthLoading(false));
        setInitialized(true);
      }
    } catch (err) {
      // console.error("❌ App init failed:", err);
      dispatch(setAuthLoading(false));
      setInitialized(true);
    } finally {
      setTimeout(() => {
        initiating.current = false;
      }, 1000);
    }
  };

  useEffect(() => {
    if (!initiating.current) {
      initiateApp();
    }
  }, []);

  // // Add this new useEffect to handle page reload
  // useEffect(() => {
  //   const handleBeforeUnload = () => {
  //     if (socket) {
  //       socket.disconnect();
  //       socket = null;
  //     }
  //   };

  //   window.addEventListener("beforeunload", handleBeforeUnload);

  //   return () => {
  //     window.removeEventListener("beforeunload", handleBeforeUnload);
  //     handleBeforeUnload(); // Also cleanup when component unmounts
  //   };
  // }, []);

  return (
    <>
      {!loading && initialized ? <IRouter /> : "Loading.."}
      <InstallPromptModal
        open={
          (isInstallable || isManualInstall) &&
          !isDismissed &&
          !isInstalled &&
          user?.ssoEnabled
        }
        onInstall={promptInstall}
        onDismiss={dismissInstall}
        onDone={markAsInstalled}
        error={installError}
        isManualInstall={isManualInstall}
        isIos={isIos}
        isSafari={isSafari}
        isIosChrome={isIosChrome}
        isMac={isMac}
      />
    </>
  );
}

export { socket };
export default App;
